import { useCallback, useEffect, useState } from 'react'
import { FileText, Trash2, Upload } from 'lucide-react'
import { SettingsSection } from '~/components/common/SettingsSection'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import {
  type CodingGuidelinesMetadata,
  deleteCodingGuidelines,
  getCodingGuidelines,
  uploadCodingGuidelinesWithMetadata
} from '~/services/organizationService'
import { BetterToast } from '~/utils/toast'

interface CodingGuidelinesUploadProps {
  onProgress?: (progress: number) => void
  onComplete?: (uploadedPath: string) => void
  className?: string
}

export function CodingGuidelinesUpload({
  onProgress = () => {},
  onComplete = () => {}
}: CodingGuidelinesUploadProps) {
  const { currentWorkspace } = useWorkspaceContext()
  const workspaceUuid = currentWorkspace?.uuid
  const [isLoading, setIsLoading] = useState(true)
  const [existingGuidelines, setExistingGuidelines] = useState<CodingGuidelinesMetadata | null>(
    null
  )

  useEffect(() => {
    const loadExistingGuidelines = async () => {
      if (!workspaceUuid) {
        setIsLoading(false)
        return
      }

      try {
        const response = await getCodingGuidelines(workspaceUuid)
        if (response.coding_guidelines) {
          setExistingGuidelines(response.coding_guidelines)
        }
      } catch (error) {
        console.error('Failed to load existing coding guidelines:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadExistingGuidelines()
  }, [workspaceUuid])

  const handleUpload = useCallback(() => {
    if (!workspaceUuid) {
      BetterToast.error('Workspace not available')
      return
    }

    const fileInput = document.createElement('input')
    fileInput.type = 'file'
    fileInput.accept = '.pdf'

    fileInput.onchange = async (e: Event) => {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (!file) return

      if (file.size > 5 * 1024 * 1024) {
        BetterToast.error('Maximum file size exceeded (5MB)')
        return
      }

      uploadCodingGuidelinesWithMetadata(workspaceUuid, file, {
        onProgress: (progress: number) => {
          onProgress(progress)
        },
        onComplete: async (uploadedPath: string) => {
          try {
            const response = await getCodingGuidelines(workspaceUuid)
            if (response.coding_guidelines) {
              setExistingGuidelines(response.coding_guidelines)
            }
          } catch (error) {
            console.error('Failed to reload guidelines metadata:', error)
          }
          BetterToast.success('File uploaded successfully')
          onComplete(uploadedPath)
        },
        onError: (error: string) => {
          BetterToast.error(`Upload failed: ${error}`)
        },
        onVirusScanStatus: () => {}
      })
    }

    fileInput.click()
  }, [workspaceUuid, onComplete, onProgress])

  const handleRemoveFile = useCallback(async () => {
    if (!workspaceUuid) return

    try {
      await deleteCodingGuidelines(workspaceUuid)
      setExistingGuidelines(null)
      onComplete('')
      BetterToast.success('File removed')
    } catch (error) {
      console.error('Failed to delete coding guidelines:', error)
      BetterToast.error('Failed to remove file')
    }
  }, [workspaceUuid, onComplete])

  if (isLoading) {
    return null
  }

  if (existingGuidelines) {
    return (
      <SettingsSection>
        <div className='group flex items-center gap-4 py-4'>
          <span className='flex items-center justify-center w-6 h-6 shrink-0 text-tokens-primary-2'>
            <FileText className='w-[18px] h-[18px]' />
          </span>
          <span className='font-mono font-semibold text-[12px] leading-normal text-tokens-primary-2 px-2'>
            {existingGuidelines.filename}
          </span>
          <button
            type='button'
            onClick={handleRemoveFile}
            className='flex items-center justify-center w-6 h-6 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer hover:text-tokens-status-error-text text-tokens-neutral-icon-solid'
            aria-label={`Remove ${existingGuidelines.filename}`}
          >
            <Trash2 className='w-[18px] h-[18px]' />
          </button>
        </div>
      </SettingsSection>
    )
  }

  return (
    <SettingsSection>
      <div
        role='button'
        tabIndex={0}
        onClick={handleUpload}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            handleUpload()
          }
        }}
        className='group flex items-center gap-4 py-2 w-full cursor-pointer'
      >
        <span className='flex items-center justify-center w-6 h-6 shrink-0 text-tokens-neutral-icon-solid'>
          <Upload className='w-[18px] h-[18px]' />
        </span>
        <div className='flex flex-col items-start gap-0.5 px-2'>
          <span className='font-sans font-medium text-[14px] leading-[20px] tracking-[0.1px] text-[rgba(0,0,0,0.88)] group-hover:text-tokens-status-link-3 transition-colors select-none'>
            Upload Coding Guidelines
          </span>
          <span className='font-sans text-[12px] leading-[22px] text-[rgba(0,0,0,0.45)] select-none'>
            PDF file with maximum size of 5 MB
          </span>
        </div>
      </div>
    </SettingsSection>
  )
}

export default CodingGuidelinesUpload
